import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Download, Maximize2, X, ZoomIn, ZoomOut } from 'lucide-react';
import { modalBackdropCls, modalCardCls, wideOverlayCls } from './overlay';
import { downloadNodePng } from './nodeShot';
import { MADE_ID_BASE } from './subStepKinds';
import { PieceNode, type MadePiece, type RowPieces } from './SubStepChips';
import type { PartItem } from './partsRun';
import { useLanguage } from '../../contexts/LanguageContext';

// THE WHOLE MANUAL AS ONE TREE: the finished thing at the top, and under it, level by level,
// everything it was built out of — down to the parts that came in the box.
//
// It is the same drawing a violet chip opens (see PieceNode), asked of the last piece instead
// of a chosen one. What it answers is the question the sub-step table can only be added up to
// answer: a table says what each gesture takes and leaves, forty rows of it, and the shape of
// the thing being built is somewhere in the sum. Here it is the shape.
//
// A ROOT IS A PIECE NOTHING ELSE SWALLOWS. Usually one — the finished chair — and legitimately
// several while a manual is half analysed, or where it really does build two things and join
// them last. Each gets its own tree rather than being forced under an invented parent.
export function AssemblyTree({ rows, made, items, islands = rows, name = 'assembly-tree', corner }: {
  rows: { key: string }[];
  // What each gesture holds and leaves, from the one place that walks the sequence.
  made: Map<string, RowPieces>;
  items: PartItem[];
  // WHOSE PIECES THIS TREE IS ABOUT — one step's gestures, or every one of them.
  //
  // Two lists and not one, because a step's tree is not a tree of a step's pieces alone: it
  // grows down through whatever those were built out of, and half of that was assembled two
  // steps earlier. So `rows` is the whole manual, which is what a component is resolved
  // against, and this is the run of gestures whose pieces are the tops of the drawing.
  islands?: { key: string }[];
  // WHAT THE SAVED FILE IS CALLED — the position's own label, "Tree view (step 1)", which is
  // what the reader has on the screen while they click. A downloads folder with four files in
  // it called assembly-tree is a folder nobody can use.
  name?: string;
  // What sits in the card's top-right corner — the caller's choice of which tree this is.
  corner?: ReactNode;
}) {
  const { t } = useLanguage();
  // Opened large, over everything. Escape closes it, as every other window here does.
  const [big, setBig] = useState(false);
  // THE DRAWING AT ITS OWN SIZE, which is what is saved — never the shrunk copy in the card,
  // and never the window's, which only exists while the window is open. One element, in the
  // card, always mounted; the scale standing on it is dropped by the copy that is painted.
  const row = useRef<HTMLDivElement>(null);
  const save = () => { if (row.current) void downloadNodePng(row.current, name); };
  useEffect(() => {
    if (!big) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setBig(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [big]);

  // Every piece the manual makes, by its own id — the aliases the row maps also carry are
  // deliberately not here: a component is resolved by a real id and nothing else, which is
  // the rule the chip's own tree learnt the hard way.
  const pieces = useMemo(() => {
    const out = new Map<string, MadePiece>();
    for (const { key } of rows) {
      const row = made.get(key);
      if (row?.own) out.set(row.own.id, row.own);
    }
    return out;
  }, [rows, made]);

  // THE PIECES NOTHING ELSE SWALLOWS, in the order they were made.
  //
  // There should be one — the finished thing — and there are four, because a gesture that
  // builds on what the last one left does not always say so: "assembled chair with inserted
  // screw" comes back made of a seat and a screw, with no mention of the chair. The prompt
  // asks for it (a piece picked up is named by its id in "parts"); the model forgets, and the
  // sequence falls into islands.
  //
  // SO THEY ARE CHAINED, oldest into newest: an island made after another one is built on it,
  // which is what "assembled chair with felt pads" then "assembled chair with inserted screw"
  // can only mean. It is an inference, and it is the difference between one assembly and four
  // orphans.
  //
  // IT USED TO SAY SO UNDER THE DRAWING, a line of italics on every tree with more than one
  // island — which is most of them, on every page of the walk, saying the same thing about a
  // shape the reader has already taken in. A caveat that never varies is read once and is
  // furniture after that.
  const { root, all } = useMemo(() => {
    // WHAT THIS SCOPE SWALLOWS, and only that. A piece assembled in step 2 and taken into
    // step 3 is not a root of the manual, and IS the root of step 2's own tree — the claim
    // "nothing else swallows it" has to be made about the same run of gestures the tree is
    // drawn of, or a step's tree would come out headless.
    const mine = new Map<string, MadePiece>();
    for (const { key } of islands) {
      const row = made.get(key);
      if (row?.own) mine.set(row.own.id, row.own);
    }
    const swallowed = new Set<string>();
    for (const piece of mine.values()) {
      for (const part of piece.from) {
        if (Number(part.id) >= MADE_ID_BASE) swallowed.add(part.id);
      }
    }
    // The islands, in the manual's own order: the rows are the sequence, and a piece is made
    // where its maker stands in it.
    const order: MadePiece[] = [];
    for (const { key } of islands) {
      const row = made.get(key);
      if (row?.own && !swallowed.has(row.own.id)) order.push(row.own);
    }
    if (!order.length) return { root: null, all: pieces };
    // WHAT THIS SCOPE MAY EXPAND. A step's tree grows down through what its own gestures
    // took in, and a piece made in ANOTHER step is where it stops: 112 belongs to step 3, and
    // opening it inside step 4's tree draws step 3's fork, its sub-step badge and its cycle
    // under a heading that says step 4. The piece is still named — it is what step 4 works on
    // — it simply has no branches here.
    //
    // Everything is in scope for the complete tree, where `islands` is every row.
    const inScope = new Set<string>();
    for (const { key } of islands) {
      const row = made.get(key);
      if (row?.own) inScope.add(row.own.id);
    }
    // Each island takes the one before it as a component, so the last of them is the whole
    // assembly. Into a map of its own: the originals are what every chip elsewhere draws,
    // and nothing outside this tree should see an edge nobody stated.
    const chained = new Map(
      [...pieces].map(([id, piece]) => [
        id,
        inScope.has(id) ? piece : { ...piece, from: [], cycle: null },
      ]),
    );
    order.forEach((piece, i) => {
      if (i === 0) return;
      chained.set(piece.id, {
        ...piece,
        from: [{ id: order[i - 1].id, qty: 1 }, ...piece.from],
      });
    });
    return { root: chained.get(order[order.length - 1].id) ?? null, all: chained };
  }, [pieces, islands, made]);

  // Nothing to draw — and the corner still there, so a step with no tree is a choice you can
  // take back rather than a dead end.
  if (!root) {
    return (
      <div className="flex flex-col items-center gap-2">
        {corner && <div className="self-end">{corner}</div>}
        <p className="text-center text-sm text-slate-400">
          {t('notice.steps.treeNone')}
        </p>
      </div>
    );
  }

  // The drawing itself, so the panel and the window that opens it are the same picture and
  // not two of them — shown whole in one, read at its true size in the other.
  const drawing = (
    <PieceNode
      node={{ id: root.id, qty: 1, piece: root }}
      items={items}
      pieces={all}
      seen={new Set([root.id])}
    />
  );

  return (
    <>
      {/* A CARD, not the page. The tree is a diagram — lines, forks, chips — and drawn
          straight onto the phase's background it reads as scattered labels rather than as one
          object. The card is what says the drawing ends here. */}
      <div className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        {corner && <div className="absolute right-3 top-3 z-10">{corner}</div>}
        <FitToBox rowRef={row} resetKey={`${name}:${root.id}`}>{drawing}</FitToBox>
        {/* THE SAME PICTURE, THE WHOLE WIDTH OF THE SCREEN. A manual of a dozen gestures is a
            tree wider than the column it is read in, and sideways scrolling is a poor way to
            look at a shape — you cannot see the two ends of it at once, which is the one
            thing a tree is for. */}
        <div className="absolute bottom-3 right-3 flex items-center gap-2">
          {/* AND THE SAME PICTURE AS A FILE. A tree is the one thing on this page that ends up
              in somebody else's document, and there is nothing here to right-click: it is
              boxes and rules, not an image. */}
          <button
            type="button"
            onClick={save}
            title={t('notice.steps.treeSave')}
            aria-label={t('notice.steps.treeSave')}
            className={cornerBtn}
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setBig(true)}
            title={t('notice.steps.treeZoom')}
            aria-label={t('notice.steps.treeZoom')}
            className={cornerBtn}
          >
            <Maximize2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {big && createPortal(
        <div className={`${wideOverlayCls} ${modalBackdropCls}`} onClick={() => setBig(false)}>
          <div
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            className={`relative max-h-[92vh] w-full max-w-[96vw] overflow-auto p-6 ${modalCardCls}`}
          >
            <div className="absolute right-3 top-3 flex items-center gap-2">
              <button
                type="button"
                onClick={save}
                title={t('notice.steps.treeSave')}
                aria-label={t('notice.steps.treeSave')}
                className="rounded-lg p-1 text-slate-400 transition-colors hover:text-blue-600"
              >
                <Download className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => setBig(false)}
                aria-label={t('notice.pdf.close')}
                className="rounded-lg p-1 text-slate-400 transition-colors hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {/* Sideways where a level is wider than the window, and downwards where the tree
                is taller than it: this is the drawing at reading size, and nothing here may
                squeeze a chip to avoid a scrollbar. */}
            <div className="overflow-x-auto">
              <div className={ROW}>{drawing}</div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}


// THE BUTTONS IN THE CARD'S CORNER, which are two and must read as a pair: same size, same
// weight, same distance from the drawing. Written once for that reason.
const cornerBtn = 'rounded-lg border border-slate-300 bg-white p-2 text-slate-500 shadow-sm transition-colors hover:border-blue-500 hover:text-blue-600';

// ONE ROW OF THE DRAWING: as wide as the tree, and never narrower than what holds it — a tree
// is as wide as its widest level, and where that is narrower than the frame it is centred in it.
const ROW = 'flex min-w-max justify-center px-2 pb-2';

// HOW MUCH OF THE SCREEN THE CARD MAY TAKE. Most of it — a whole tree shrunk into a strip says
// nothing — but not all of it: the step's own controls sit above and below the card and have to
// stay reachable without scrolling past a diagram.
const headroom = () => Math.max(320, Math.round(window.innerHeight * 0.66));

// THE WHOLE SHAPE, AT WHATEVER SIZE IT TAKES TO SHOW ALL OF IT.
//
// Written one gesture at a time, a step is thirty pieces deep, and at reading size that is a
// card several screens tall: the reader gets the top four chips and scrolls blind for the rest.
// The shape is the one thing a tree is for and the one thing that view cannot show. So the card
// fits the drawing to itself, always the whole of it, and the button in the corner opens the
// same drawing at its true size — which is where it is read, a part at a time.
//
// NOTHING HERE CAN CHASE ITS OWN TAIL, which a measure-and-resize pair otherwise always can. A
// transform is paint and not layout: `offsetWidth` is the drawing's natural size whatever scale
// is standing on it, so the measurement that decides the scale cannot be moved by the scale it
// decided. The height that follows goes on the frame, and the frame's width — the only other
// input — does not depend on its height. Measurements are compared before they are stored, so
// an observer that fires on its own effect settles instead of running.
function FitToBox({ rowRef, resetKey, children }: {
  // The drawing's own element, held by the caller: what is measured here is also what is
  // saved, and there is no second copy of it to disagree with.
  rowRef: RefObject<HTMLDivElement | null>;
  // WHICH DRAWING THIS IS — the one and only thing that throws a zoom away. Keyed on the
  // measured size instead, a resize fired for any reason (a font settling, a sub-pixel
  // rounding flip) would snap the zoom back mid-gesture, which is indistinguishable from
  // "the zoom does not work".
  resetKey: string;
  children: ReactNode;
}) {
  const { t } = useLanguage();
  const frame = useRef<HTMLDivElement>(null);
  const row = rowRef;
  const [size, setSize] = useState({ frame: 0, w: 0, h: 0 });
  const [room, setRoom] = useState(headroom);

  // THE INTERACTIVE PART, layered on top of the fit scale rather than replacing it: `zoom` is
  // a multiplier on it (1 = the whole tree exactly fits, and the floor) and `pan` is a manual
  // offset in screen pixels applied after scaling. Both reset when the drawing itself changes
  // and on nothing else.
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [resetKey]);

  // A NATIVE LISTENER, not React's `onWheel`: React's is passive, and a passive listener
  // cannot call `preventDefault` — the page would scroll AND the tree would zoom off the one
  // gesture. `deltaY` through `exp` so the same flick feels the same at either end of the
  // range; a linear step shrinks to nothing when zoomed out and overshoots when zoomed in.
  //
  // 0.006 rather than something smaller because a trackpad reports a handful of pixels per
  // event where a mouse wheel's notch is nearer 100 — tuned so a two-finger swipe actually
  // moves it.
  useEffect(() => {
    const el = frame.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = Math.exp(-e.deltaY * 0.006);
      setZoom((z) => Math.min(8, Math.max(1, z * factor)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
    setDragging(true);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragRef.current) return;
    setPan({
      x: dragRef.current.panX + (e.clientX - dragRef.current.x),
      y: dragRef.current.panY + (e.clientY - dragRef.current.y),
    });
  };
  const endDrag = () => {
    dragRef.current = null;
    setDragging(false);
  };

  useEffect(() => {
    const onResize = () => setRoom((was) => {
      const now = headroom();
      return now === was ? was : now;
    });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const box = frame.current;
    const el = row.current;
    if (!box || !el) return undefined;
    const measure = () => {
      const next = { frame: box.clientWidth, w: el.offsetWidth, h: el.offsetHeight };
      setSize((was) => (
        was.frame === next.frame && was.w === next.w && was.h === next.h ? was : next
      ));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // THE FIT SCALE IS THE FLOOR, not just the starting point. `zoom` multiplies it, and 1
  // means "the whole tree exactly fits" — so zooming OUT stops there rather than carrying on
  // into a tree adrift in empty space. There is nothing to see past the whole shape, and a
  // gesture that keeps going after the answer is reached only has to be undone.
  const fitScale = Math.min(1, size.w ? size.frame / size.w : 1, size.h ? room / size.h : 1);
  const scale = fitScale * zoom;

  // The frame is exactly the drawing at its FIT height — the height it has when all of it is
  // visible, which is the only height it ever needs, because zoom cannot go below that. Zoom
  // in and the drawing grows past the frame and is panned within it; the card itself never
  // changes size, so reading the tree does not make the page jump.
  const frameH = size.h ? Math.round(size.h * fitScale) : 0;

  // Centred by hand, because a scaled box still occupies its full width in the layout: the
  // flexbox around it would centre the space the drawing used to take, not the drawing.
  //
  // NOT clamped at 0. `(frame - drawn) / 2` is negative once the drawing is wider than the
  // frame, and that negative IS the centring — the drawing hanging equally off both edges.
  // Clamped up to 0 it pinned its top-left corner to the frame's instead, so growing the
  // scale walked the drawing left and up out of view: zooming appeared to pan. Left signed,
  // the frame's centre stays the centre at every scale, which is what zooming in place is.
  const baseLeft = (size.frame - size.w * scale) / 2;
  const baseTop = (frameH - size.h * scale) / 2;

  // AND THE PAN IS CLAMPED TO THE DRAWING'S OWN EDGES: only the overflow is draggable, per
  // axis. At the fit scale nothing overflows, so nothing moves — dragging the whole tree off
  // into the corner of an empty card is not a view of anything. Applied on render rather
  // than in the drag handler, so a zoom OUT pulls a pan that has become illegal back with it.
  const slackX = Math.max(0, (size.w * scale - size.frame) / 2);
  const slackY = Math.max(0, (size.h * scale - frameH) / 2);
  const panX = Math.min(slackX, Math.max(-slackX, pan.x));
  const panY = Math.min(slackY, Math.max(-slackY, pan.y));
  const canPan = slackX > 0 || slackY > 0;

  return (
    <div
      ref={frame}
      className={`relative touch-none overflow-hidden ${
        canPan ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : ''
      }`}
      style={{ height: frameH || undefined }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
    >
      <div
        ref={row}
        className={ROW}
        style={{
          transform: `translate(${baseLeft + panX}px, ${baseTop + panY}px) scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
      {/* THE RELIABLE WAY IN, always on screen — not only when the wheel gesture happens to
          land cleanly. A trackpad's wheel events run through momentum phases a browser can
          decline to let `preventDefault` touch; a click has no such phase. No RESET button
          beside them: zooming out already stops at the whole tree, so the view a reset would
          return to is one click of zoom-out away.

          `stopPropagation` on pointerdown, or the click also reaches the frame's own handler
          (these are its descendants), which takes pointer capture for a drag — and the click
          becomes the start of a pan instead. */}
      <div
        className="absolute bottom-2 left-2 flex items-center gap-1"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setZoom((z) => Math.min(8, z * 1.4))}
          title={t('notice.steps.treeZoomIn')}
          aria-label={t('notice.steps.treeZoomIn')}
          className={zoomBtn}
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setZoom((z) => Math.max(1, z / 1.4))}
          disabled={zoom <= 1}
          title={t('notice.steps.treeZoomOut')}
          aria-label={t('notice.steps.treeZoomOut')}
          className={zoomBtn}
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// The pair in the drawing's corner, written once so they read as one control.
const zoomBtn =
  'flex items-center justify-center rounded-md border border-slate-300 bg-white/90 p-1.5 text-slate-600 shadow-sm backdrop-blur transition-colors hover:border-blue-500 hover:text-blue-600 disabled:opacity-40 disabled:hover:border-slate-300 disabled:hover:text-slate-600';
